/**
 * AYU Survey Schema – 4 Sektionen
 * Basierend auf MENTO/GENO-Struktur, angepasst für AYU-Ayurveda-Kontext
 *
 * Sektionen:
 *   1. "Deine Ziele"       (goals)     – 6 Fragen
 *   2. "Dein Körper"       (body)      – 40 Fragen
 *   3. "Dein Wohlbefinden" (wellbeing) – 12 Fragen
 *   4. "Dein Lebensstil"   (lifestyle) – 10 Fragen
 *
 * Gesamt: ~68 Fragen (exkl. bedingte Unterfragen)
 */

// ─── Types ───

export interface SubQuestion {
  id: string;
  text: string;
  type: "text" | "select" | "radio" | "multiselect";
  placeholder?: string;
  options?: { value: string; label: string }[];
  condition?: {
    parentValue: string | string[];
  };
}

export interface SurveyQuestion {
  id: string;
  section: "goals" | "body" | "wellbeing" | "lifestyle";
  category: string;
  label: string;
  text: string;
  type: "radio" | "multiselect" | "text" | "range" | "number" | "range_input" | "yesno" | "frequency" | "select" | "preference";
  options?: { value: string; label: string; description?: string; icon?: string }[];
  min?: number;
  max?: number;
  step?: number;
  minLabel?: string;
  maxLabel?: string;
  fromLabel?: string;
  toLabel?: string;
  unit?: string;
  placeholder?: string;
  required?: boolean;
  subQuestions?: SubQuestion[];
  showOtherInput?: boolean;
}

export interface SurveyAnswer {
  questionId: string;
  value: string | number | string[];
  otherText?: string;
  subAnswers?: Record<string, string | number | string[]>;
}

export interface SurveyResponse {
  goals: SurveyAnswer[];
  body: SurveyAnswer[];
  wellbeing: SurveyAnswer[];
  lifestyle: SurveyAnswer[];
  completedAt?: number;
  completedSections?: string[];
}

// ─── SECTION 1: DEINE ZIELE (6 Fragen) ───

const goalsQuestions: SurveyQuestion[] = [
  {
    id: "g1",
    section: "goals",
    category: "Ziele",
    label: "Gesundheitsziele",
    text: "Was sind deine Hauptziele?",
    type: "multiselect",
    required: true,
    options: [
      { value: "energy", label: "Mehr Energie", icon: "⚡", description: "Vitalität und Leistungsfähigkeit steigern" },
      { value: "digestion", label: "Bessere Verdauung", icon: "🌿", description: "Darmgesundheit und Wohlbefinden" },
      { value: "sleep", label: "Besserer Schlaf", icon: "😴", description: "Erholsamer und tiefer schlafen" },
      { value: "stress", label: "Weniger Stress", icon: "🧘", description: "Innere Ruhe und Gelassenheit" },
      { value: "weight", label: "Gewichtsmanagement", icon: "⚖️", description: "Gesundes Gewicht erreichen" },
      { value: "skin", label: "Bessere Haut", icon: "✨", description: "Strahlende, gesunde Haut" },
      { value: "immunity", label: "Stärkeres Immunsystem", icon: "🛡️", description: "Abwehrkräfte stärken" },
      { value: "mental", label: "Mentale Klarheit", icon: "🧠", description: "Fokus und geistige Schärfe" },
      { value: "longevity", label: "Langlebigkeit", icon: "🌱", description: "Gesund alt werden" },
      { value: "other", label: "Sonstiges", icon: "💫" },
    ],
    showOtherInput: true,
  },
  {
    id: "g2",
    section: "goals",
    category: "Ziele",
    label: "Hauptanliegen",
    text: "Was ist dein wichtigstes gesundheitliches Anliegen?",
    type: "text",
    placeholder: "Beschreibe in einem Satz, was du am meisten verändern möchtest...",
    required: true,
  },
  {
    id: "g3",
    section: "goals",
    category: "Erfahrung",
    label: "Ayurveda-Erfahrung",
    text: "Hast du bereits Erfahrung mit Ayurveda?",
    type: "radio",
    options: [
      { value: "none", label: "Nein, das ist neu für mich" },
      { value: "basic", label: "Grundwissen (Bücher, Videos)" },
      { value: "moderate", label: "Einige Erfahrung (Kuren, Beratung)" },
      { value: "experienced", label: "Viel Erfahrung" },
    ],
  },
  {
    id: "g4",
    section: "goals",
    category: "Erfahrung",
    label: "Meditation/Achtsamkeit",
    text: "Hast du Erfahrung mit Meditation oder Achtsamkeitsübungen?",
    type: "radio",
    options: [
      { value: "none", label: "Keine Erfahrung" },
      { value: "beginner", label: "Anfänger" },
      { value: "regular", label: "Regelmäßig" },
      { value: "advanced", label: "Fortgeschritten" },
    ],
  },
  {
    id: "g5",
    section: "goals",
    category: "Erfahrung",
    label: "Therapie-Erfahrung",
    text: "Hast du Erfahrung mit Therapie oder gesundheitlicher Beratung?",
    type: "radio",
    options: [
      { value: "no", label: "Nein" },
      { value: "yes", label: "Ja, in der Vergangenheit" },
      { value: "current", label: "Ja, aktuell in Behandlung" },
    ],
    subQuestions: [
      {
        id: "g5_detail",
        text: "Magst du kurz beschreiben, welche Art?",
        type: "text",
        placeholder: "Optional — hilft AYU, dich besser zu verstehen.",
        condition: { parentValue: ["yes", "current"] },
      },
    ],
  },
  {
    id: "g6",
    section: "goals",
    category: "Präferenzen",
    label: "Kommunikation",
    text: "Wie möchtest du am liebsten mit AYU arbeiten?",
    type: "radio",
    options: [
      { value: "text", label: "Text-Chat", description: "Schreiben und Lesen" },
      { value: "voice", label: "Sprache", description: "Sprechen und Hören" },
      { value: "both", label: "Beides", description: "Je nach Situation" },
    ],
  },
];

// ─── SECTION 2: DEIN KÖRPER (40 Fragen) ───

const bodyQuestions: SurveyQuestion[] = [
  // Grunddaten
  {
    id: "b1",
    section: "body",
    category: "Persönliches",
    label: "Altersgruppe",
    text: "In welcher Altersgruppe bist du?",
    type: "radio",
    required: true,
    options: [
      { value: "18-24", label: "18–24" },
      { value: "25-29", label: "25–29" },
      { value: "30-39", label: "30–39" },
      { value: "40-49", label: "40–49" },
      { value: "50-59", label: "50–59" },
      { value: "60+", label: "60+" },
    ],
  },
  {
    id: "b2",
    section: "body",
    category: "Persönliches",
    label: "Geschlecht",
    text: "Geschlecht",
    type: "radio",
    required: false,
    options: [
      { value: "male", label: "Männlich" },
      { value: "female", label: "Weiblich" },
      { value: "diverse", label: "Divers" },
      { value: "prefer_not_to_say", label: "Keine Angabe" },
    ],
  },
  {
    id: "b3",
    section: "body",
    category: "Persönliches",
    label: "Größe",
    text: "Wie groß bist du (in cm)?",
    type: "number",
    min: 100,
    max: 230,
    placeholder: "z.B. 175",
  },
  {
    id: "b4",
    section: "body",
    category: "Persönliches",
    label: "Gewicht",
    text: "Wie viel wiegst du (in kg)?",
    type: "number",
    min: 30,
    max: 250,
    placeholder: "z.B. 70",
  },
  {
    id: "b5",
    section: "body",
    category: "Persönliches",
    label: "Körperbau",
    text: "Wie würdest du deinen Körperbau beschreiben?",
    type: "radio",
    options: [
      { value: "slim", label: "Schlank / zierlich" },
      { value: "athletic", label: "Sportlich / muskulös" },
      { value: "medium", label: "Durchschnittlich" },
      { value: "sturdy", label: "Kräftig / stämmig" },
      { value: "heavy", label: "Schwer / füllig" },
    ],
  },
  {
    id: "b6",
    section: "body",
    category: "Persönliches",
    label: "Gewichtsveränderung",
    text: "Wie verhält sich dein Gewicht?",
    type: "radio",
    options: [
      { value: "gain_easy", label: "Nehme leicht zu" },
      { value: "lose_easy", label: "Nehme leicht ab" },
      { value: "stable", label: "Relativ stabil" },
      { value: "fluctuate", label: "Schwankt stark" },
    ],
  },
  // Ernährung
  {
    id: "b7",
    section: "body",
    category: "Ernährung",
    label: "Ernährungstyp",
    text: "Wie ernährst du dich überwiegend?",
    type: "radio",
    options: [
      { value: "mixed", label: "Mischkost" },
      { value: "vegetarian", label: "Vegetarisch" },
      { value: "vegan", label: "Vegan" },
      { value: "flexitarian", label: "Flexitarisch" },
      { value: "other", label: "Andere" },
    ],
    showOtherInput: true,
  },
  {
    id: "b8",
    section: "body",
    category: "Ernährung",
    label: "Ernährungsbewusstsein",
    text: "Wie bewusst achtest du auf deine Ernährung?",
    type: "radio",
    options: [
      { value: "not_at_all", label: "Kaum" },
      { value: "moderate", label: "Etwas" },
      { value: "very", label: "Sehr bewusst" },
    ],
  },
  {
    id: "b9",
    section: "body",
    category: "Ernährung",
    label: "Mahlzeiten",
    text: "Wie viele Mahlzeiten isst du am Tag?",
    type: "radio",
    options: [
      { value: "1-2", label: "1–2 Mahlzeiten" },
      { value: "3", label: "3 Mahlzeiten" },
      { value: "4+", label: "4 oder mehr" },
      { value: "irregular", label: "Unregelmäßig" },
    ],
  },
  {
    id: "b10",
    section: "body",
    category: "Ernährung",
    label: "Trinkmenge",
    text: "Wie viel Wasser/Flüssigkeit trinkst du täglich?",
    type: "radio",
    options: [
      { value: "less_1l", label: "Weniger als 1 Liter" },
      { value: "1_2l", label: "1–2 Liter" },
      { value: "2_3l", label: "2–3 Liter" },
      { value: "more_3l", label: "Mehr als 3 Liter" },
    ],
  },
  // Verdauung
  {
    id: "b11",
    section: "body",
    category: "Verdauung",
    label: "Verdauung nach dem Essen",
    text: "Wie fühlst du dich nach dem Essen?",
    type: "multiselect",
    options: [
      { value: "energized", label: "Energiegeladen" },
      { value: "tired", label: "Müde / schläfrig" },
      { value: "bloated", label: "Aufgebläht" },
      { value: "heavy", label: "Schwer im Magen" },
      { value: "acidic", label: "Sodbrennen / sauer" },
      { value: "fine", label: "Normal / unauffällig" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  {
    id: "b12",
    section: "body",
    category: "Verdauung",
    label: "Stuhlgang",
    text: "Wie ist dein Stuhlgang normalerweise?",
    type: "radio",
    options: [
      { value: "regular", label: "Regelmäßig und normal" },
      { value: "constipation", label: "Eher Verstopfung" },
      { value: "loose", label: "Eher weich / durchfallartig" },
      { value: "alternating", label: "Wechselnd" },
      { value: "irregular", label: "Unregelmäßig" },
    ],
  },
  {
    id: "b13",
    section: "body",
    category: "Verdauung",
    label: "Blähungen",
    text: "Wie oft hast du Blähungen?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Fast immer" },
    ],
  },
  {
    id: "b14",
    section: "body",
    category: "Verdauung",
    label: "Unverträglichkeiten",
    text: "Hast du Nahrungsmittelunverträglichkeiten?",
    type: "yesno",
    subQuestions: [
      {
        id: "b14_detail",
        text: "Welche?",
        type: "text",
        placeholder: "z.B. Laktose, Gluten, Histamin...",
        condition: { parentValue: "yes" },
      },
    ],
  },
  // Haut
  {
    id: "b15",
    section: "body",
    category: "Haut",
    label: "Hauttyp",
    text: "Wie würdest du deinen Hauttyp beschreiben?",
    type: "radio",
    options: [
      { value: "dry", label: "Trocken" },
      { value: "oily", label: "Fettig / ölig" },
      { value: "combination", label: "Mischhaut" },
      { value: "sensitive", label: "Empfindlich" },
      { value: "normal", label: "Normal" },
    ],
  },
  {
    id: "b16",
    section: "body",
    category: "Haut",
    label: "Hautprobleme",
    text: "Hast du eines dieser Hautthemen?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine" },
      { value: "acne", label: "Akne / Unreinheiten" },
      { value: "eczema", label: "Ekzeme / Neurodermitis" },
      { value: "redness", label: "Rötungen / Rosazea" },
      { value: "dryness", label: "Starke Trockenheit" },
      { value: "sun_sensitivity", label: "Sonnenempfindlichkeit" },
      { value: "pigmentation", label: "Pigmentflecken" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  // Temperatur & Immunsystem
  {
    id: "b17",
    section: "body",
    category: "Konstitution",
    label: "Temperaturempfinden",
    text: "Wie empfindest du Temperaturen?",
    type: "radio",
    options: [
      { value: "cold_sensitive", label: "Eher kälteempfindlich" },
      { value: "heat_sensitive", label: "Eher hitzeempfindlich" },
      { value: "balanced", label: "Ausgeglichen" },
      { value: "fluctuating", label: "Schwankend" },
    ],
  },
  {
    id: "b18",
    section: "body",
    category: "Immunsystem",
    label: "Allergien",
    text: "Hast du Allergien?",
    type: "yesno",
    subQuestions: [
      {
        id: "b18_detail",
        text: "Welche?",
        type: "text",
        placeholder: "z.B. Pollen, Hausstaubmilben, Tierhaare...",
        condition: { parentValue: "yes" },
      },
    ],
  },
  {
    id: "b19",
    section: "body",
    category: "Immunsystem",
    label: "Infektanfälligkeit",
    text: "Wie oft bist du krank (Erkältungen, Infekte)?",
    type: "radio",
    options: [
      { value: "never", label: "Nie (0 Mal im Jahr)" },
      { value: "rarely", label: "Selten (1–2x im Jahr)" },
      { value: "normal", label: "Normal (3–4x im Jahr)" },
      { value: "often", label: "Häufig (5+ mal im Jahr)" },
      { value: "chronic", label: "Chronische Beschwerden" },
    ],
  },
  {
    id: "b20",
    section: "body",
    category: "Immunsystem",
    label: "Autoimmun",
    text: "Gibt es Autoimmunerkrankungen in deiner Familie?",
    type: "yesno",
    subQuestions: [
      {
        id: "b20_detail",
        text: "Welche?",
        type: "text",
        condition: { parentValue: "yes" },
      },
    ],
  },
  // Schlaf
  {
    id: "b21",
    section: "body",
    category: "Schlaf",
    label: "Schlafdauer",
    text: "Wie viele Stunden schläfst du durchschnittlich?",
    type: "range_input",
    min: 2,
    max: 14,
    fromLabel: "Von",
    toLabel: "Bis",
    placeholder: "z.B. 6–8",
    unit: "Stunden",
  },
  {
    id: "b22",
    section: "body",
    category: "Schlaf",
    label: "Schlafqualität",
    text: "Wie würdest du deine Schlafqualität bewerten?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr schlecht",
    maxLabel: "Ausgezeichnet",
  },
  {
    id: "b23",
    section: "body",
    category: "Schlaf",
    label: "Einschlafen",
    text: "Hast du Probleme beim Einschlafen?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Fast immer" },
    ],
  },
  {
    id: "b24",
    section: "body",
    category: "Schlaf",
    label: "Durchschlafen",
    text: "Wachst du nachts häufig auf?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Fast jede Nacht" },
    ],
  },
  // Bewegung + Mobilität
  {
    id: "b25",
    section: "body",
    category: "Bewegung",
    label: "Sportfrequenz",
    text: "Wie oft treibst du Sport oder bewegst dich bewusst?",
    type: "radio",
    options: [
      { value: "never", label: "Gar nicht" },
      { value: "1_2", label: "1–2x pro Woche" },
      { value: "3_4", label: "3–4x pro Woche" },
      { value: "4_5", label: "4–5x pro Woche" },
      { value: "daily", label: "Täglich" },
    ],
  },
  {
    id: "b26",
    section: "body",
    category: "Bewegung",
    label: "Sportart",
    text: "Welche Art von Bewegung bevorzugst du?",
    type: "multiselect",
    options: [
      { value: "yoga", label: "Yoga / Stretching" },
      { value: "walking", label: "Spazieren / Wandern" },
      { value: "running", label: "Laufen / Joggen" },
      { value: "gym", label: "Krafttraining" },
      { value: "swimming", label: "Schwimmen" },
      { value: "cycling", label: "Radfahren" },
      { value: "dance", label: "Tanzen" },
      { value: "martial_arts", label: "Kampfsport" },
      { value: "none", label: "Keine regelmäßige Bewegung" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  // NEU: Mobilitäts-Frage
  {
    id: "b27",
    section: "body",
    category: "Bewegung",
    label: "Körperliche Einschränkungen",
    text: "Hast du körperliche Einschränkungen, die bei Übungen berücksichtigt werden sollten?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine Einschränkungen" },
      { value: "mobility", label: "Mobilitätseinschränkung (z.B. Rollstuhl, Gehilfe)" },
      { value: "chronic_pain", label: "Chronische Schmerzen" },
      { value: "limited_flexibility", label: "Eingeschränkte Beweglichkeit" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  // Medikation & Familie
  {
    id: "b28",
    section: "body",
    category: "Medikation",
    label: "Medikamente",
    text: "Nimmst du regelmäßig Medikamente ein?",
    type: "yesno",
    subQuestions: [
      {
        id: "b28_detail",
        text: "Welche Medikamente?",
        type: "text",
        condition: { parentValue: "yes" },
      },
    ],
  },
  // ─── NEU: Fehlende Fragen aus Original-Fragebogen (Hybrid-Ansatz) ───
  // Herkunft & Abstammung
  {
    id: "b29",
    section: "body",
    category: "Persönliches",
    label: "Herkunft",
    text: "Woher stammt deine Familie ursprünglich?",
    type: "multiselect",
    options: [
      { value: "europe_central", label: "Mitteleuropa" },
      { value: "europe_south", label: "Südeuropa / Mittelmeerraum" },
      { value: "europe_north", label: "Nordeuropa / Skandinavien" },
      { value: "europe_east", label: "Osteuropa" },
      { value: "middle_east", label: "Naher Osten" },
      { value: "south_asia", label: "Südasien (Indien, Pakistan, Sri Lanka)" },
      { value: "east_asia", label: "Ostasien (China, Japan, Korea)" },
      { value: "southeast_asia", label: "Südostasien" },
      { value: "africa", label: "Afrika" },
      { value: "americas", label: "Nord-/Südamerika" },
      { value: "mixed", label: "Gemischt / mehrere Regionen" },
      { value: "unknown", label: "Weiß ich nicht" },
    ],
  },
  // Probiotika
  {
    id: "b30",
    section: "body",
    category: "Verdauung",
    label: "Probiotika",
    text: "Wann hast du zuletzt Probiotika eingenommen?",
    type: "radio",
    options: [
      { value: "currently", label: "Nehme ich aktuell" },
      { value: "recently", label: "In den letzten 3 Monaten" },
      { value: "past_year", label: "Innerhalb des letzten Jahres" },
      { value: "long_ago", label: "Länger als 1 Jahr her" },
      { value: "never", label: "Noch nie" },
    ],
  },
  // Kaiserschnitt
  {
    id: "b31",
    section: "body",
    category: "Immunsystem",
    label: "Geburtsart",
    text: "Wurdest du per Kaiserschnitt geboren?",
    type: "radio",
    options: [
      { value: "yes", label: "Ja" },
      { value: "no", label: "Nein" },
      { value: "unknown", label: "Weiß ich nicht" },
    ],
  },
  // Alkohol
  {
    id: "b32",
    section: "body",
    category: "Lebensmittel",
    label: "Alkohol",
    text: "Wie oft trinkst du Alkohol?",
    type: "radio",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten (1–2x im Monat)" },
      { value: "weekly", label: "Wöchentlich" },
      { value: "several_weekly", label: "Mehrmals pro Woche" },
      { value: "daily", label: "Täglich" },
    ],
  },
  // Rauchen
  {
    id: "b33",
    section: "body",
    category: "Lebensmittel",
    label: "Rauchen",
    text: "Rauchst du?",
    type: "radio",
    options: [
      { value: "no", label: "Nein, nie geraucht" },
      { value: "former", label: "Ehemaliger Raucher" },
      { value: "occasionally", label: "Gelegentlich" },
      { value: "regularly", label: "Regelmäßig" },
    ],
    subQuestions: [
      {
        id: "b33_detail",
        text: "Wie viele Zigaretten pro Tag?",
        type: "text",
        placeholder: "z.B. 10",
        condition: { parentValue: "regularly" },
      },
    ],
  },
  // Chronisch-entzündliche Darmerkrankungen
  {
    id: "b34",
    section: "body",
    category: "Verdauung",
    label: "Chronisch-entzündliche Darmerkrankung",
    text: "Leidest du an einer chronisch-entzündlichen Darmerkrankung (CED/IBD)?",
    type: "yesno",
    subQuestions: [
      {
        id: "b34_detail",
        text: "Welche?",
        type: "multiselect",
        options: [
          { value: "crohn", label: "Morbus Crohn" },
          { value: "colitis", label: "Colitis ulcerosa" },
          { value: "ibs", label: "Reizdarmsyndrom (IBS)" },
          { value: "other", label: "Sonstiges" },
        ],
        condition: { parentValue: "yes" },
      },
    ],
  },
  // Diagnostizierte Erkrankungen
  {
    id: "b35",
    section: "body",
    category: "Medikation",
    label: "Diagnostizierte Erkrankungen",
    text: "Wurden bei dir medizinische Erkrankungen diagnostiziert?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine" },
      { value: "diabetes", label: "Diabetes" },
      { value: "thyroid", label: "Schilddrüsenerkrankung" },
      { value: "hypertension", label: "Bluthochdruck" },
      { value: "heart", label: "Herz-Kreislauf-Erkrankung" },
      { value: "autoimmune", label: "Autoimmunerkrankung" },
      { value: "depression_anxiety", label: "Depression / Angststörung" },
      { value: "cancer", label: "Krebserkrankung" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  // Antibiotika
  {
    id: "b36",
    section: "body",
    category: "Medikation",
    label: "Antibiotika",
    text: "Wann hast du zuletzt Antibiotika eingenommen?",
    type: "radio",
    options: [
      { value: "recently", label: "In den letzten 3 Monaten" },
      { value: "past_year", label: "Innerhalb des letzten Jahres" },
      { value: "1_3_years", label: "Vor 1–3 Jahren" },
      { value: "long_ago", label: "Länger als 3 Jahre her" },
      { value: "never", label: "Noch nie" },
    ],
  },
  // Infektionskrankheiten
  {
    id: "b37",
    section: "body",
    category: "Immunsystem",
    label: "Infektionskrankheiten",
    text: "Leidest du an Infektionskrankheiten?",
    type: "yesno",
    subQuestions: [
      {
        id: "b37_detail",
        text: "Welche?",
        type: "multiselect",
        options: [
          { value: "herpes", label: "Herpes" },
          { value: "hepatitis", label: "Hepatitis" },
          { value: "borreliosis", label: "Borreliose" },
          { value: "epstein_barr", label: "Epstein-Barr-Virus (EBV)" },
          { value: "hiv", label: "HIV" },
          { value: "other", label: "Sonstiges" },
        ],
        condition: { parentValue: "yes" },
      },
    ],
  },
  // Lebensmittel-Präferenzen (kompakte Matrix)
  {
    id: "b38",
    section: "body",
    category: "Ernährung",
    label: "Lebensmittel-Präferenzen",
    text: "Welche Lebensmittelgruppen isst du gerne? (Wähle alle, die du regelmäßig isst)",
    type: "multiselect",
    options: [
      { value: "red_meat", label: "Rotes Fleisch (Rind, Schwein, Lamm)" },
      { value: "poultry", label: "Geflügel" },
      { value: "fish", label: "Fisch" },
      { value: "seafood", label: "Meeresfrüchte" },
      { value: "dairy", label: "Milchprodukte (Käse, Joghurt, Milch)" },
      { value: "eggs", label: "Eier" },
      { value: "legumes", label: "Hülsenfrüchte (Linsen, Bohnen, Kichererbsen)" },
      { value: "whole_grains", label: "Vollkornprodukte" },
      { value: "white_flour", label: "Weißmehlprodukte" },
      { value: "rice_pasta", label: "Reis / Nudeln" },
      { value: "fruits", label: "Obst" },
      { value: "vegetables", label: "Gemüse" },
      { value: "nuts_seeds", label: "Nüsse / Samen" },
      { value: "fermented", label: "Fermentierte Lebensmittel (Kimchi, Sauerkraut, Kombucha)" },
      { value: "plant_milk", label: "Pflanzenmilch (Hafer, Mandel, Soja)" },
      { value: "sweets", label: "Süßigkeiten / Zucker" },
      { value: "processed", label: "Fertiggerichte / Fast Food" },
    ],
  },
  // Ernährungsfrequenz
  {
    id: "b39",
    section: "body",
    category: "Ernährung",
    label: "Ernährungsfrequenz",
    text: "Wie oft isst du folgende Lebensmittel?",
    type: "multiselect",
    options: [
      { value: "meat_daily", label: "Fleisch: täglich" },
      { value: "meat_weekly", label: "Fleisch: 1–3x pro Woche" },
      { value: "meat_rarely", label: "Fleisch: selten oder nie" },
      { value: "fish_weekly", label: "Fisch: mindestens 1x pro Woche" },
      { value: "fish_rarely", label: "Fisch: selten oder nie" },
      { value: "dairy_daily", label: "Milchprodukte: täglich" },
      { value: "dairy_rarely", label: "Milchprodukte: selten oder nie" },
      { value: "fruit_veg_daily", label: "Obst & Gemüse: täglich" },
      { value: "fruit_veg_rarely", label: "Obst & Gemüse: selten" },
    ],
  },
  // Reisen (Immunsystem-relevant)
  {
    id: "b40",
    section: "body",
    category: "Immunsystem",
    label: "Reisen",
    text: "Bist du in den letzten 6 Monaten außerhalb Europas gereist?",
    type: "yesno",
    subQuestions: [
      {
        id: "b40_detail",
        text: "Wohin?",
        type: "text",
        placeholder: "z.B. Indien, Thailand, Südamerika...",
        condition: { parentValue: "yes" },
      },
    ],
  },
];

// ─── SECTION 3: DEIN WOHLBEFINDEN (12 Fragen) ───

const wellbeingQuestions: SurveyQuestion[] = [
  {
    id: "w1",
    section: "wellbeing",
    category: "Stress",
    label: "Stresslevel",
    text: "Wie hoch ist dein aktuelles Stresslevel?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Kaum Stress",
    maxLabel: "Sehr gestresst",
    required: true,
  },
  {
    id: "w2",
    section: "wellbeing",
    category: "Stress",
    label: "Stressreaktion",
    text: "Wie reagierst du typischerweise auf Stress?",
    type: "multiselect",
    options: [
      { value: "anxious", label: "Ängstlich / nervös" },
      { value: "angry", label: "Gereizt / wütend" },
      { value: "withdrawn", label: "Rückzug / Isolation" },
      { value: "overthinking", label: "Grübeln / Überdenken" },
      { value: "physical", label: "Körperliche Symptome (Kopfschmerz, Magen...)" },
      { value: "calm", label: "Bleibe relativ ruhig" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  {
    id: "w3",
    section: "wellbeing",
    category: "Emotionen",
    label: "Stimmung",
    text: "Wie würdest du deine allgemeine Stimmung beschreiben?",
    type: "radio",
    options: [
      { value: "positive", label: "Überwiegend positiv" },
      { value: "neutral", label: "Neutral / wechselnd" },
      { value: "low", label: "Eher niedergeschlagen" },
      { value: "anxious", label: "Ängstlich / unruhig" },
      { value: "fluctuating", label: "Stark schwankend" },
    ],
  },
  {
    id: "w4",
    section: "wellbeing",
    category: "Emotionen",
    label: "Emotionale Stabilität",
    text: "Wie emotional stabil fühlst du dich im Alltag?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr instabil",
    maxLabel: "Sehr stabil",
  },
  {
    id: "w5",
    section: "wellbeing",
    category: "Mental",
    label: "Konzentration",
    text: "Wie gut kannst du dich konzentrieren?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr schlecht",
    maxLabel: "Sehr gut",
  },
  {
    id: "w6",
    section: "wellbeing",
    category: "Mental",
    label: "Gedankenkarussell",
    text: "Leidest du unter kreisenden Gedanken oder Grübeln?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Ständig" },
    ],
  },
  {
    id: "w7",
    section: "wellbeing",
    category: "Emotionen",
    label: "Dominante Emotion",
    text: "Welche Emotion begleitet dich am häufigsten?",
    type: "radio",
    options: [
      { value: "joy", label: "Freude / Zufriedenheit" },
      { value: "worry", label: "Sorge / Angst" },
      { value: "anger", label: "Ärger / Frustration" },
      { value: "sadness", label: "Traurigkeit" },
      { value: "restlessness", label: "Unruhe / Nervosität" },
      { value: "numbness", label: "Leere / Gleichgültigkeit" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  {
    id: "w8",
    section: "wellbeing",
    category: "Emotionen",
    label: "Coping",
    text: "Was hilft dir am meisten, wenn es dir nicht gut geht?",
    type: "multiselect",
    options: [
      { value: "nature", label: "Natur / Spazieren" },
      { value: "talking", label: "Gespräche mit Vertrauten" },
      { value: "exercise", label: "Sport / Bewegung" },
      { value: "meditation", label: "Meditation / Atemübungen" },
      { value: "creative", label: "Kreative Tätigkeiten" },
      { value: "rest", label: "Ruhe / Schlaf" },
      { value: "food", label: "Essen / Kochen" },
      { value: "nothing", label: "Nichts hilft wirklich" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  {
    id: "w9",
    section: "wellbeing",
    category: "Energie",
    label: "Energielevel",
    text: "Wie ist dein allgemeines Energielevel?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr niedrig",
    maxLabel: "Voller Energie",
  },
  {
    id: "w10",
    section: "wellbeing",
    category: "Energie",
    label: "Erschöpfung",
    text: "Wie oft fühlst du dich erschöpft?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Ständig" },
    ],
  },
  {
    id: "w11",
    section: "wellbeing",
    category: "Energie",
    label: "Morgens",
    text: "Wie fühlst du dich morgens nach dem Aufwachen?",
    type: "radio",
    options: [
      { value: "refreshed", label: "Ausgeruht und energisch" },
      { value: "ok", label: "Ok, brauche etwas Zeit" },
      { value: "tired", label: "Müde trotz Schlaf" },
      { value: "heavy", label: "Schwer und träge" },
      { value: "anxious", label: "Unruhig / ängstlich" },
    ],
  },
  {
    id: "w12",
    section: "wellbeing",
    category: "Selbstwahrnehmung",
    label: "Körperbewusstsein",
    text: "Wie gut nimmst du Signale deines Körpers wahr?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Kaum",
    maxLabel: "Sehr gut",
  },
];

// ─── SECTION 4: DEIN LEBENSSTIL (10 Fragen) ───

const lifestyleQuestions: SurveyQuestion[] = [
  {
    id: "ls1",
    section: "lifestyle",
    category: "Tagesroutine",
    label: "Aufstehzeit",
    text: "Wann stehst du normalerweise auf?",
    type: "radio",
    required: true,
    options: [
      { value: "before_6", label: "Vor 6:00 Uhr" },
      { value: "6_7", label: "6:00–7:00 Uhr" },
      { value: "7_8", label: "7:00–8:00 Uhr" },
      { value: "8_9", label: "8:00–9:00 Uhr" },
      { value: "after_9", label: "Nach 9:00 Uhr" },
    ],
  },
  {
    id: "ls2",
    section: "lifestyle",
    category: "Tagesroutine",
    label: "Schlafenszeit",
    text: "Wann gehst du normalerweise schlafen?",
    type: "radio",
    options: [
      { value: "before_22", label: "Vor 22:00 Uhr" },
      { value: "22_23", label: "22:00–23:00 Uhr" },
      { value: "23_0", label: "23:00–0:00 Uhr" },
      { value: "0_1", label: "0:00–1:00 Uhr" },
      { value: "after_1", label: "Nach 1:00 Uhr" },
    ],
  },
  {
    id: "ls3",
    section: "lifestyle",
    category: "Tagesroutine",
    label: "Morgenroutine",
    text: "Hast du eine feste Morgenroutine?",
    type: "radio",
    options: [
      { value: "structured", label: "Ja, sehr strukturiert" },
      { value: "some", label: "Teilweise" },
      { value: "none", label: "Nein, eher chaotisch" },
      { value: "varies", label: "Variiert stark" },
    ],
  },
  {
    id: "ls4",
    section: "lifestyle",
    category: "Beruf",
    label: "Arbeitsbelastung",
    text: "Wie empfindest du deine Arbeitsbelastung?",
    type: "radio",
    options: [
      { value: "low", label: "Gering" },
      { value: "moderate", label: "Angemessen" },
      { value: "high", label: "Hoch" },
      { value: "overwhelming", label: "Überwältigend" },
      { value: "not_working", label: "Arbeite aktuell nicht" },
    ],
  },
  {
    id: "ls5",
    section: "lifestyle",
    category: "Beruf",
    label: "Zeitdruck",
    text: "Wie oft stehst du unter Zeitdruck?",
    type: "frequency",
    options: [
      { value: "never", label: "Nie" },
      { value: "rarely", label: "Selten" },
      { value: "sometimes", label: "Manchmal" },
      { value: "often", label: "Häufig" },
      { value: "always", label: "Ständig" },
    ],
  },
  {
    id: "ls6",
    section: "lifestyle",
    category: "Freizeit",
    label: "Lebensereignisse",
    text: "Gibt es aktuelle Lebensereignisse, die dich beschäftigen?",
    type: "multiselect",
    required: false,
    options: [
      { value: "none", label: "Keine besonderen" },
      { value: "job_change", label: "Jobwechsel / Karriere" },
      { value: "relationship_change", label: "Beziehungsveränderung" },
      { value: "health_issue", label: "Gesundheitliches Thema" },
      { value: "loss", label: "Verlust / Trauer" },
      { value: "relocation", label: "Umzug" },
      { value: "parenthood", label: "Elternschaft" },
      { value: "financial", label: "Finanzielle Sorgen" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
  {
    id: "ls7",
    section: "lifestyle",
    category: "Freizeit",
    label: "Erholung",
    text: "Wie gut kannst du dich in deiner Freizeit erholen?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Kaum Erholung",
    maxLabel: "Sehr gut",
  },
  {
    id: "ls8",
    section: "lifestyle",
    category: "Balance",
    label: "Work-Life-Balance",
    text: "Wie zufrieden bist du mit deiner Work-Life-Balance?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr unzufrieden",
    maxLabel: "Sehr zufrieden",
  },
  {
    id: "ls9",
    section: "lifestyle",
    category: "Balance",
    label: "Lebensqualität",
    text: "Wie zufrieden bist du insgesamt mit deiner Lebensqualität?",
    type: "range",
    min: 1,
    max: 10,
    minLabel: "Sehr unzufrieden",
    maxLabel: "Sehr zufrieden",
  },
  {
    id: "ls10",
    section: "lifestyle",
    category: "Familiengeschichte",
    label: "Familiäre Erkrankungen",
    text: "Gibt es häufige Erkrankungen in deiner Familie?",
    type: "multiselect",
    options: [
      { value: "none", label: "Keine bekannten" },
      { value: "diabetes", label: "Diabetes" },
      { value: "hypertension", label: "Bluthochdruck" },
      { value: "heart", label: "Herz-Kreislauf" },
      { value: "cancer", label: "Krebs" },
      { value: "autoimmune", label: "Autoimmun" },
      { value: "mental", label: "Psychische Erkrankungen" },
      { value: "digestive", label: "Verdauungsprobleme" },
      { value: "allergies", label: "Allergien" },
      { value: "skin", label: "Hauterkrankungen" },
      { value: "other", label: "Sonstiges" },
    ],
    showOtherInput: true,
  },
];

// ─── EXPORT ───

export const SURVEY_SECTIONS = {
  goals: {
    id: "goals" as const,
    title: "Deine Ziele",
    subtitle: "Was möchtest du mit AYU erreichen?",
    description: "Erzähle uns von deinen Gesundheitszielen und Erfahrungen.",
    icon: "🎯",
    questions: goalsQuestions,
  },
  body: {
    id: "body" as const,
    title: "Dein Körper",
    subtitle: "Körper, Ernährung & Gesundheit",
    description: "Erzähle uns von deinem Körper, deiner Ernährung und deiner gesundheitlichen Geschichte.",
    icon: "💪",
    questions: bodyQuestions,
  },
  wellbeing: {
    id: "wellbeing" as const,
    title: "Dein Wohlbefinden",
    subtitle: "Geist, Emotionen & Energie",
    description: "Wie geht es dir mental und emotional? Was gibt dir Kraft?",
    icon: "🌱",
    questions: wellbeingQuestions,
  },
  lifestyle: {
    id: "lifestyle" as const,
    title: "Dein Lebensstil",
    subtitle: "Alltag, Beruf & Balance",
    description: "Wie sieht dein Alltag aus? Wie gestaltest du dein Leben?",
    icon: "🏃",
    questions: lifestyleQuestions,
  },
};

export const ALL_QUESTIONS = [
  ...goalsQuestions,
  ...bodyQuestions,
  ...wellbeingQuestions,
  ...lifestyleQuestions,
];

/**
 * Convert survey answers to readable text for LLM prompt context.
 */
export function surveyToPromptContext(response: SurveyResponse): string {
  const lines: string[] = [];

  const formatAnswer = (answer: SurveyAnswer, question: SurveyQuestion): string => {
    const q = question.text;
    let a: string;

    if (Array.isArray(answer.value)) {
      const labels = answer.value.map((v) => {
        const opt = question.options?.find((o) => o.value === v);
        return opt?.label || v;
      });
      a = labels.join(", ");
    } else if (question.options) {
      const opt = question.options.find((o) => o.value === String(answer.value));
      a = opt?.label || String(answer.value);
    } else if (question.type === "yesno") {
      a = answer.value === "yes" ? "Ja" : "Nein";
    } else {
      a = String(answer.value);
    }

    let result = `${q} → ${a}`;

    // Other text
    if (answer.otherText) {
      result += `\n  ↳ Sonstiges: ${answer.otherText}`;
    }

    // Sub-answers
    if (answer.subAnswers) {
      for (const [subId, subVal] of Object.entries(answer.subAnswers)) {
        const subQ = question.subQuestions?.find((sq) => sq.id === subId);
        if (subQ && subVal) {
          result += `\n  ↳ ${subQ.text} → ${subVal}`;
        }
      }
    }

    return result;
  };

  const sections = [
    { key: "goals" as const, title: "ZIELE" },
    { key: "body" as const, title: "KÖRPER" },
    { key: "wellbeing" as const, title: "WOHLBEFINDEN" },
    { key: "lifestyle" as const, title: "LEBENSSTIL" },
  ];

  for (const section of sections) {
    const answers = response[section.key];
    if (!answers || answers.length === 0) continue;

    lines.push(`\n--- ${section.title} ---`);
    for (const answer of answers) {
      const question = ALL_QUESTIONS.find((q) => q.id === answer.questionId);
      if (question) {
        lines.push(formatAnswer(answer, question));
      }
    }
  }

  return lines.join("\n");
}
